import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Hls from 'hls.js';
import {
  StackedCarousel,
  ResponsiveContainer,
  StackedCarouselSlideProps,
} from 'react-stacked-center-carousel';
import SpeakToExpertButton from 'src/components/home/SpeakToExpertButton';
import VideoModal from 'src/components/home/VideoModal';
import VideoControls from 'src/components/home/VideoControls';
import { generateVideoDataArray, initHLSOnVideo } from 'src/utils/videoUtils';
import { VideoSources } from 'src/types/video';
import { useIntersectionObserver } from 'src/hooks/useIntersectionObserver';
import { EXTERNAL_LINKS } from 'src/constants';

const SLIDE_ASPECT_RATIO = 199 / 308;

const MAX_SLIDE_WIDTH = 320;
const MAX_SLIDE_HEIGHT = 500;

const MIN_SLIDE_HEIGHT = 296;
const MIN_SLIDE_WIDTH = Math.round(MIN_SLIDE_HEIGHT * SLIDE_ASPECT_RATIO);

function computeSlideWidth(viewportWidth: number): number {
  if (viewportWidth >= 1024) return MAX_SLIDE_WIDTH;
  const scaled = Math.round((viewportWidth / 640) * 199);
  return Math.max(MIN_SLIDE_WIDTH, Math.min(199, scaled));
}

function useWindowWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

interface SlideCustomProps {
  slideWidth: number;
  slideHeight: number;
  onOpenModal: (sources: VideoSources, currentTime: number) => void;
  playingVideoId: number | null;
  videoProgress: Record<number, number>;
  loadingStates: Record<number, boolean>;
  loadedStates: Record<number, boolean>;
  onTogglePlay: (videoId: number, video: HTMLVideoElement | null) => void;
  onProgressUpdate: (videoId: number, progress: number) => void;
  onLoadingChange: (videoId: number, isLoading: boolean) => void;
  onLoadedChange: (videoId: number, isLoaded: boolean) => void;
}

interface VideoData {
  id: number;
  sources: VideoSources;
  customProps?: SlideCustomProps;
}

// Generate video data using the utility function
const BASE_VIDEOS = generateVideoDataArray('edit');

const Slide = React.memo(function Slide(
  props: StackedCarouselSlideProps
) {
  const { data, dataIndex, isCenterSlide, swipeTo, slideIndex } = props;
  const videoData = (data as VideoData[])[dataIndex];
  const customProps = videoData.customProps;
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const slideWidth = customProps?.slideWidth ?? MAX_SLIDE_WIDTH;
  const slideHeight = customProps?.slideHeight ?? MAX_SLIDE_HEIGHT;
  const isPlaying = customProps?.playingVideoId === videoData.id;
  const progress = customProps?.videoProgress[videoData.id] ?? 0;
  const isLoading = customProps?.loadingStates[videoData.id] ?? false;
  const isLoaded = customProps?.loadedStates[videoData.id] ?? false;

  // Cleanup HLS instance on unmount
  useEffect(() => {
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, []);

  // Load video source with HLS (or fallback)
  const loadVideoSource = useCallback(() => {
    const video = videoRef.current;
    if (!video || isLoaded) return;

    // Initialize HLS with automatic fallback
    hlsRef.current = initHLSOnVideo(video, videoData.sources);
    customProps?.onLoadedChange(videoData.id, true);
  }, [videoData.sources, videoData.id, isLoaded, customProps]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.duration) {
        const progressPercent = (video.currentTime / video.duration) * 100;
        customProps?.onProgressUpdate(videoData.id, progressPercent);
      }
    };

    const handleEnded = () => {
      customProps?.onProgressUpdate(videoData.id, 0);
      customProps?.onTogglePlay(videoData.id, null);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [customProps, videoData.id]);

  // Handle play/pause state changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      // Load source if not already loaded
      if (!isLoaded) {
        loadVideoSource();
      }
      video.play().catch((e) => console.debug('Video play prevented:', e));
    } else {
      video.pause();
    }
  }, [isPlaying, isLoaded, loadVideoSource]);

  const handleClick = useCallback(() => {
    if (!isCenterSlide) {
      swipeTo(slideIndex);
      return;
    }
    // Load video source on first click if not loaded
    if (!isLoaded) {
      loadVideoSource();
    }
    // Toggle play/pause for center slide
    customProps?.onTogglePlay(videoData.id, videoRef.current);
  }, [isCenterSlide, slideIndex, swipeTo, customProps, videoData.id, isLoaded, loadVideoSource]);

  const handleFullscreen = useCallback(() => {
    const currentTime = videoRef.current?.currentTime ?? 0;
    customProps?.onOpenModal(videoData.sources, currentTime);
  }, [customProps, videoData.sources]);

  const handleWaiting = useCallback(() => {
    customProps?.onLoadingChange(videoData.id, true);
  }, [customProps, videoData.id]);

  const handleCanPlay = useCallback(() => {
    customProps?.onLoadingChange(videoData.id, false);
  }, [customProps, videoData.id]);

  const handleTogglePlay = useCallback(() => {
    if (!isLoaded) {
      loadVideoSource();
    }
    customProps?.onTogglePlay(videoData.id, videoRef.current);
  }, [customProps, videoData.id, isLoaded, loadVideoSource]);

  const borderRadius = Math.round((slideWidth / MAX_SLIDE_WIDTH) * 29.41);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div
        className="overflow-hidden relative shadow-[0_10px_40px_rgba(0,0,0,0.15)] cursor-pointer transition-all duration-300"
        onClick={handleClick}
        style={{ width: slideWidth, height: slideHeight, borderRadius }}
      >
        <video
          ref={videoRef}
          poster={videoData.sources.poster}
          className="w-full h-full object-cover"
          playsInline
          preload="none"
          onWaiting={handleWaiting}
          onCanPlay={handleCanPlay}
          onLoadedData={handleCanPlay}
        />

        <VideoControls
          variant="card"
          isPlaying={isPlaying}
          isLoading={isLoading}
          progress={progress}
          onTogglePlay={handleTogglePlay}
          onFullscreen={handleFullscreen}
        />
      </div>
    </div>
  );
});

const XploriusEditSlider = () => {
  const { t, i18n } = useTranslation('home');
  const isRu = i18n.language === 'ru'
  const carouselRef = useRef<StackedCarousel>();
  const viewportWidth = useWindowWidth();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoSources, setModalVideoSources] = useState<VideoSources | null>(null);
  const [modalStartTime, setModalStartTime] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [videoProgress, setVideoProgress] = useState<Record<number, number>>({});
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>({});
  const [loadedStates, setLoadedStates] = useState<Record<number, boolean>>({});

  // Use intersection observer to pause videos when section is out of view
  const { ref: sectionRef, isVisible } = useIntersectionObserver<HTMLElement>({
    threshold: 0.1,
    rootMargin: '0px',
  });

  // Pause playing video when section leaves viewport
  useEffect(() => {
    if (!isVisible && playingVideoId !== null) {
      setPlayingVideoId(null);
    }
  }, [isVisible, playingVideoId]);

  const slideWidth = computeSlideWidth(viewportWidth);
  const slideHeight = viewportWidth >= 1024
    ? MAX_SLIDE_HEIGHT
    : Math.round(slideWidth / SLIDE_ASPECT_RATIO);

  const handleOpenModal = useCallback((sources: VideoSources, currentTime: number) => {
    setPlayingVideoId(null);
    setModalVideoSources(sources);
    setModalStartTime(currentTime);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalVideoSources(null);
    setModalStartTime(0);
  }, []);

  const handleTogglePlay = useCallback((videoId: number, video: HTMLVideoElement | null) => {
    if (video === null) {
      setPlayingVideoId(null);
      return;
    }
    setPlayingVideoId((current) => (current === videoId ? null : videoId));
  }, []);

  const handleProgressUpdate = useCallback((videoId: number, progress: number) => {
    setVideoProgress((prev) => ({ ...prev, [videoId]: progress }));
  }, []);

  const handleLoadingChange = useCallback((videoId: number, isLoading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [videoId]: isLoading }));
  }, []);

  const handleLoadedChange = useCallback((videoId: number, isLoaded: boolean) => {
    setLoadedStates((prev) => ({ ...prev, [videoId]: isLoaded }));
  }, []);

  const customProps: SlideCustomProps = {
    slideWidth,
    slideHeight,
    onOpenModal: handleOpenModal,
    playingVideoId,
    videoProgress,
    loadingStates,
    loadedStates,
    onTogglePlay: handleTogglePlay,
    onProgressUpdate: handleProgressUpdate,
    onLoadingChange: handleLoadingChange,
    onLoadedChange: handleLoadedChange,
  };
  const videos: VideoData[] = BASE_VIDEOS.map((v) => ({ ...v, customProps }));

  const visibleSlides = 5;
  const scalesCount = (visibleSlides + 3) / 2;
  const customScales = Array.from({ length: scalesCount }, (_, i) =>
    Math.max(0.5, 1 - i * 0.15)
  );

  return (
    <section ref={sectionRef} className="xplorius-edit lg:!py-[60px] sm:!py-10 !py-8 bg-white">
      <div className="max-w-[2000px] mx-auto">
        <div className="text-center mb-6 sm:mb-8 lg:mb-10">
          <h2 className="section-heading mb-2 lg:mb-4">
            {t('xploriusEdit.heading')}
          </h2>

          <p className="text-xs sm:text-lg text-[#4A4A4A] font-normal leading-[22px] lg:leading-[30px]">
            <Trans
              i18nKey="xploriusEdit.subheading"
              ns="home"
              components={{ br1: <br className='sm:hidden' /> }}
            />
          </p>
        </div>

        <div className="relative transition-all duration-300 overflow-hidden">
          <ResponsiveContainer
            carouselRef={carouselRef}
            render={(parentWidth, carouselRef) => (
              <StackedCarousel
                ref={carouselRef}
                data={videos}
                carouselWidth={parentWidth}
                slideWidth={slideWidth}
                slideComponent={Slide}
                maxVisibleSlide={visibleSlides}
                currentVisibleSlide={visibleSlides}
                useGrabCursor={false}
                disableSwipe={true}
                customScales={customScales}
              />
            )}
          />
        </div>

        <div className={`text-center mt-6 sm:mt-8 lg:mt-11 flex justify-center ${isRu ? 'flex-col' : 'flex-col-reverse sm:flex-col'} gap-4 sm:gap-6 lg:gap-10 items-center`}>
          <p className='text-[#4A4A4A] text-xs sm:text-base lg:text-lg text-center'>
            <Trans
              i18nKey="xploriusEdit.instagramCta"
              ns="home"
              components={{
                a1: <a href={EXTERNAL_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="hover:underline font-semibold [-webkit-tap-highlight-color:transparent] hover:text-[#4A4A4A]">i</a>,
              }}
            />
          </p>

          <SpeakToExpertButton />
        </div>
      </div>

      <VideoModal
        sources={modalVideoSources}
        isOpen={modalOpen}
        onClose={handleCloseModal}
        startTime={modalStartTime}
      />
    </section>
  );
};

export default XploriusEditSlider;
